import React, { useState } from 'react';
import { Shield, Mail, Lock, Smartphone, CheckCircle, XCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { sendEmailVerification, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';

/**
 * Security Settings Component
 * Manages user security settings including email verification, password changes, and MFA
 */
const SecuritySettings = () => {
  const { currentUser } = useAuth();
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState('idle'); // idle, changing, success, error
  const [passwordError, setPasswordError] = useState('');
  const [emailVerificationStatus, setEmailVerificationStatus] = useState('idle'); // idle, sending, sent, error

  // Handle email verification
  const handleSendVerification = async () => {
    if (!currentUser) return;

    try {
      setEmailVerificationStatus('sending');
      await sendEmailVerification(currentUser);
      setEmailVerificationStatus('sent');
      setTimeout(() => {
        setEmailVerificationStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error sending verification email:', error);
      setEmailVerificationStatus('error');
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('Please fill in all password fields');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setPasswordStatus('changing');

      // Re-authenticate user before password change
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, passwordData.newPassword);

      setPasswordStatus('success');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordChange(false);

      setTimeout(() => {
        setPasswordStatus('idle');
      }, 3000);

    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordStatus('error');

      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('Password is too weak. Use at least 6 characters');
      } else if (error.code === 'auth/requires-recent-login') {
        setPasswordError('Please log out and log back in before changing your password');
      } else {
        setPasswordError('Failed to change password. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Security Settings</h3>
          <p className="text-sm text-gray-600">Manage your account security and authentication</p>
        </div>
      </div>

      {/* Success Message */}
      {passwordStatus === 'success' && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-green-900">Password changed successfully!</p>
            <p className="text-sm text-green-800 mt-1">Your password has been updated.</p>
          </div>
        </div>
      )}

      {/* Email Verification */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <h4 className="font-semibold text-gray-900">Email Verification</h4>
              <p className="text-sm text-gray-600 mt-1">{currentUser?.email}</p>
              {currentUser?.emailVerified ? (
                <div className="flex items-center space-x-2 mt-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">Verified</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 mt-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-700 font-medium">Not Verified</span>
                </div>
              )}
            </div>
          </div>

          {!currentUser?.emailVerified && (
            <button
              onClick={handleSendVerification}
              disabled={emailVerificationStatus === 'sending' || emailVerificationStatus === 'sent'}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                emailVerificationStatus === 'sent'
                  ? 'bg-green-100 text-green-700'
                  : emailVerificationStatus === 'sending'
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {emailVerificationStatus === 'sending' ? 'Sending...' : emailVerificationStatus === 'sent' ? 'Email Sent!' : 'Verify Email'}
            </button>
          )}
        </div>

        {emailVerificationStatus === 'sent' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Verification email sent! Check your inbox and click the verification link.
            </p>
          </div>
        )}
      </div>

      {/* Password Change */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3">
            <Lock className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <h4 className="font-semibold text-gray-900">Password</h4>
              <p className="text-sm text-gray-600 mt-1">
                {showPasswordChange ? 'Update your password' : 'Change your account password'}
              </p>
            </div>
          </div>

          {!showPasswordChange && (
            <button
              onClick={() => setShowPasswordChange(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
            >
              Change Password
            </button>
          )}
        </div>

        {showPasswordChange && (
          <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
            {passwordError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{passwordError}</p>
              </div>
            )}

            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter current password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter new password (min 6 characters)"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm new password"
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={passwordStatus === 'changing'}
                className={`flex-1 py-2 rounded-lg font-semibold text-white transition ${
                  passwordStatus === 'changing'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                }`}
              >
                {passwordStatus === 'changing' ? 'Changing...' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordChange(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordError('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Multi-Factor Authentication (Coming Soon) */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-dashed border-gray-300 relative overflow-hidden">
        <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
          COMING SOON
        </div>
        <div className="flex items-start space-x-3 opacity-60">
          <Smartphone className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-900">Two-Factor Authentication (2FA)</h4>
            <p className="text-sm text-gray-600 mt-1">
              Add an extra layer of security by requiring a second form of authentication
            </p>
            <div className="mt-4 space-y-2">
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-gray-400" />
                <span>SMS verification code</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-gray-400" />
                <span>Authenticator app (Google Authenticator, Authy)</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-gray-400" />
                <span>Backup codes for account recovery</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            disabled
            className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm font-semibold cursor-not-allowed"
          >
            Enable 2FA (Coming Soon)
          </button>
        </div>
      </div>

      {/* Security Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-semibold text-blue-900 text-sm mb-2">Security Best Practices</h5>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Use a strong, unique password for your PlanLens account</li>
              <li>• Enable email verification to secure your account</li>
              <li>• Never share your password with anyone</li>
              <li>• Change your password regularly (every 3-6 months)</li>
              <li>• Log out from shared or public devices</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
